// apps/embed/src/chat-widget/useWebsiteChat.ts
//
// The website chat widget's state machine — the consumer-side mirror of the
// backoffice support widget's useSupportChat, but for an ANONYMOUS visitor with a
// single ongoing thread (resumed from localStorage), driven by the /v1 polling
// transport instead of backoffice server actions. The "same smooth experience"
// comes from the same shared conversation pane + the same patterns (clientMsgId,
// mergeMessagesById, the 4s-open / 20s-idle cadence).

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatThread, ChatMessage } from '@tourtask/chat-domain';
import { mergeMessagesById, isThreadClosed, isAiAnswerable } from '@tourtask/chat-domain';
import { startChatThread, sendChatMessage, pollChatThread, markChatRead } from '@tourtask/storefront/api/chat';

const POLL_OPEN_MS = 4000;
const POLL_IDLE_MS = 20000;
/** Fast poll cadence while the bot is composing a reply (the turn runs server-
 *  side AFTER the start/send response; the reply arrives via this poll). */
const POLL_BOT_TYPING_MS = 2000;
/** Give up on the typing indicator after this long — a failed bot turn appends
 *  nothing, and dots that never resolve read worse than silence. */
const BOT_TYPING_MAX_MS = 90_000;

type Phase = 'form' | 'chat';

interface StoredSession {
  sessionToken: string;
  threadId: number;
  /** Highest `threadSeq` the visitor has already read (panel open). Persisted so a
   *  page reload doesn't re-flag the whole resumed history as unread. Absent on
   *  sessions created before unread tracking — see the resume effect's baseline. */
  lastSeenSeq?: number;
}

function storageKey(bizSlug: string): string {
  return `tt:chat:${bizSlug}`;
}

function loadSession(bizSlug: string): StoredSession | null {
  try {
    const raw = window.localStorage.getItem(storageKey(bizSlug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (typeof parsed.sessionToken === 'string' && typeof parsed.threadId === 'number') {
      return {
        sessionToken: parsed.sessionToken,
        threadId: parsed.threadId,
        lastSeenSeq: typeof parsed.lastSeenSeq === 'number' ? parsed.lastSeenSeq : undefined,
      };
    }
  } catch {
    /* localStorage unavailable / malformed — start fresh */
  }
  return null;
}

function saveSession(bizSlug: string, s: StoredSession): void {
  try {
    window.localStorage.setItem(storageKey(bizSlug), JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

function clearSession(bizSlug: string): void {
  try {
    window.localStorage.removeItem(storageKey(bizSlug));
  } catch {
    /* ignore */
  }
}

// A persistent per-browser visitor id, scoped to the whole widget (NOT per
// business) so the abuse guard can recognise the same person across businesses.
// Stable across pages, sessions, and new conversations — only a cleared browser
// resets it. Keyed alongside the visitor's IP server-side.
const VISITOR_ID_KEY = 'tt:chat:visitor';

function getOrCreateVisitorId(): string | undefined {
  try {
    const existing = window.localStorage.getItem(VISITOR_ID_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    window.localStorage.setItem(VISITOR_ID_KEY, fresh);
    return fresh;
  } catch {
    // localStorage unavailable (private mode / blocked) — fall back to IP-only
    // abuse keying server-side; no client id is sent.
    return undefined;
  }
}

/** What a start sends: the pre-chat form's fields, or — for a business that does
 *  not ask for contact details first — only the first message. */
export interface PreChatForm {
  name?: string;
  email?: string;
  /** ISO country code (CHAR(2)) + local number, kept separate (never combined). */
  phoneCountryCode?: string;
  phoneNumber?: string;
  firstMessage: string;
}

/** The visitor's own message, shown at once while its round-trip is in flight
 *  (an optimistic echo). Its temporary negative id makes the real message (same
 *  clientMsgId, positive id) win in mergeMessagesById. */
function optimisticVisitorMessage(args: {
  id: number;
  threadId: number;
  threadSeq: number;
  bizId: number | null;
  clientMsgId: string;
  body: string;
}): ChatMessage {
  return {
    id: args.id,
    threadId: args.threadId,
    threadSeq: args.threadSeq,
    bizId: args.bizId,
    senderType: 'public_visitor',
    senderAgentUserId: null,
    senderBizUserId: null,
    clientMsgId: args.clientMsgId,
    body: args.body,
    attachments: [],
    createdAtUtc: new Date().toISOString(),
  };
}

export interface UseWebsiteChat {
  phase: Phase;
  thread: ChatThread | null;
  messages: ChatMessage[];
  supportOnline: boolean;
  sending: boolean;
  starting: boolean;
  /** The bot is composing a reply out-of-band (it arrives via the poll) — show
   *  the typing bubble for as long as this is true. */
  botTyping: boolean;
  error: string | null;
  /** How many starts and sends have failed: the error line is keyed by it. */
  failureCount: number;
  /** The server refused a start without contact details: the business turned "Ask for contact
   *  details first" back on after this page loaded. The pre-chat form shows from then on. */
  contactDetailsRequired: boolean;
  /** The message that start carried, for the pre-chat form to start with; null once a
   *  conversation starts or the widget goes back to a new one. */
  refusedFirstMessage: string | null;
  /** The last message reported as not sent that the server had stored after all (its answer
   *  was lost, and the poll brought it): the composer takes it back out of the box. A new
   *  object each time. */
  recoveredMessage: { text: string } | null;
  unread: number;
  closed: boolean;
  /** Resolves `false` when the conversation could not be started (`error` says why). */
  start: (form: PreChatForm) => Promise<boolean>;
  /** Resolves `false` when the message was not delivered (`error` says why). */
  send: (text: string) => Promise<boolean>;
  startNew: () => void;
}

/**
 * @param open whether the panel is expanded (drives the poll cadence + clears the
 *             unread badge).
 * @param locale the widget's `data-lang` — sent so the bot replies in the
 *               visitor's language and confirmation emails match. `null` when
 *               the host names none: nothing is sent and the server answers in
 *               the business's default language, never English by accident.
 * @param enabled whether this is a live-chat widget at all. False in the
 *             email-only / hidden modes — then the hook resumes nothing and never
 *             polls (there is no chat session to drive).
 * @param aiEnabled the bootstrap's AI flag — whether a bot answers at all. Gates
 *             the typing indicator so a human-only chat never shows dots nobody
 *             is typing behind.
 */
export function useWebsiteChat(args: {
  bizSlug: string;
  bizToken: string;
  open: boolean;
  locale: string | null;
  enabled: boolean;
  aiEnabled: boolean;
}): UseWebsiteChat {
  const { bizSlug, bizToken, open, locale, enabled, aiEnabled } = args;
  const [phase, setPhase] = useState<Phase>('form');
  const [thread, setThread] = useState<ChatThread | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [supportOnline, setSupportOnline] = useState(false);
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [botTyping, setBotTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Each failure counts, so the widget's error line is new each time (a screen reader announces it
  // even when the previous failure's line has not finished closing).
  const [failureCount, setFailureCount] = useState(0);
  const fail = useCallback((message: string) => {
    setError(message);
    setFailureCount((count) => count + 1);
  }, []);
  const [contactDetailsRequired, setContactDetailsRequired] = useState(false);
  const [refusedFirstMessage, setRefusedFirstMessage] = useState<string | null>(null);
  const [recoveredMessage, setRecoveredMessage] = useState<{ text: string } | null>(null);
  // High-water mark of the highest `threadSeq` the visitor has READ (panel open).
  // Distinct from the poll cursor (`lastSeqRef`, what we've FETCHED): the unread
  // badge counts inbound messages newer than this marker, so resuming a session
  // never re-flags already-seen history.
  const [lastSeenSeq, setLastSeenSeq] = useState(0);
  // A resumed session with no stored read-marker (created before unread tracking)
  // can't know what was read, so it baselines to the thread's current max on the
  // first populated poll — treating pre-existing history as already seen. While
  // this is pending the badge reads 0 (see `unread` below), so the old history
  // never flashes as unread before the baseline lands.
  const [pendingBaseline, setPendingBaseline] = useState(false);

  const threadIdRef = useRef<number | null>(null);
  const sessionTokenRef = useRef<string | null>(null);
  sessionTokenRef.current = sessionToken;
  // Temporary, ever-decreasing negative id for optimistic-echo placeholders so
  // the real (positive) server id always wins in mergeMessagesById.
  const optimisticIdRef = useRef(-1);
  const lastSeqRef = useRef(0);
  // High-water mark for the poll's `sinceSeq` — count ONLY real (positive-id)
  // messages, so an optimistic placeholder doesn't push the cursor past the real
  // message the server will return for it.
  lastSeqRef.current = messages.reduce((m, x) => (x.id > 0 && x.threadSeq > m ? x.threadSeq : m), 0);
  // Highest message id already REPORTED as read to the server (the business
  // console's read receipts) — avoids re-posting the same position every poll.
  const lastReportedReadIdRef = useRef(0);

  // One clientMsgId per DISTINCT pending first message: reused across failed
  // attempts (timeout / error / lost response) so the server's session-start
  // idempotency probe maps a re-submit back to the thread the earlier attempt
  // already created instead of opening a duplicate. Regenerated when the
  // visitor edits the text (a different message is a genuinely new submission);
  // cleared on success and on the new-conversation reset.
  const startAttemptRef = useRef<{ text: string; clientMsgId: string } | null>(null);
  // The same for a message sent into the conversation: the clientMsgId of the one that failed,
  // reused when the SAME text is sent again, so a send whose answer was lost (it may be stored
  // already) is returned by the server instead of stored twice. Cleared by the next send, by the
  // poll bringing that message after all, and on the reset.
  const failedSendRef = useRef<{ text: string; clientMsgId: string } | null>(null);

  const closed = thread != null && isThreadClosed(thread.status);

  // ── Bot typing indicator ─────────────────────────────────────────
  // The bot's turn runs server-side AFTER the start/send response and the reply
  // arrives via the poll — so "is the bot typing?" is client-derived: armed on a
  // successful start/send into a bot-answerable thread, dropped the moment a
  // non-visitor message lands (or the thread closes), with a hard expiry so a
  // silently-failed turn doesn't leave the dots bouncing forever.
  const botTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearBotTyping = useCallback(() => {
    if (botTypingTimerRef.current) {
      clearTimeout(botTypingTimerRef.current);
      botTypingTimerRef.current = null;
    }
    setBotTyping(false);
  }, []);

  const armBotTyping = useCallback(() => {
    if (botTypingTimerRef.current) clearTimeout(botTypingTimerRef.current);
    setBotTyping(true);
    botTypingTimerRef.current = setTimeout(() => {
      botTypingTimerRef.current = null;
      setBotTyping(false);
    }, BOT_TYPING_MAX_MS);
  }, []);

  // Clear the pending expiry on unmount so it can't fire into unmounted state.
  useEffect(
    () => () => {
      if (botTypingTimerRef.current) clearTimeout(botTypingTimerRef.current);
    },
    [],
  );

  // Drop the dots the instant a reply lands: any REAL (positive-id) message from
  // the other side newer than everything the visitor sent — or a closed thread.
  useEffect(() => {
    if (!botTyping) return;
    const newestReal = messages.reduce<ChatMessage | null>(
      (best, m) => (m.id > 0 && (best === null || m.threadSeq > best.threadSeq) ? m : best),
      null,
    );
    if (closed || (newestReal && newestReal.senderType !== 'public_visitor')) {
      clearBotTyping();
    }
  }, [messages, botTyping, closed, clearBotTyping]);

  // The unread badge: messages from the other side (not the visitor's own, and not
  // `system` notices like "an agent joined") newer than the last seq the visitor
  // has read. Derived — not an incrementing counter — so it stays consistent with
  // `messages` + the read marker after any merge/resume. Reads 0 until a legacy
  // session's baseline lands, so resumed history is never shown as unread.
  const unread = pendingBaseline
    ? 0
    : messages.reduce(
        (n, m) =>
          m.senderType !== 'public_visitor' && m.senderType !== 'system' && m.threadSeq > lastSeenSeq
            ? n + 1
            : n,
        0,
      );

  // Reset to the pre-chat form, discarding any stored session. Shared by the
  // user-facing "start a new conversation" action (after a thread closes) AND the
  // dead-session auto-heal below: a poll/send that comes back NOT_FOUND means the
  // stored thread no longer exists server-side — its 30-day token expired, the
  // signing secret rotated, the thread was deleted, or (in dev) the DB was swapped
  // for one without this thread. Rather than leave the widget permanently stuck on
  // a thread that 404s every poll, clear the stale localStorage entry and fall back
  // to the form so the visitor gets a fresh conversation. Gated strictly on
  // NOT_FOUND by the callers, so a transient OFFLINE/TIMEOUT/UPSTREAM blip (the
  // local API restarting, a flaky network) never discards a live session.
  const resetToForm = useCallback(() => {
    clearSession(bizSlug);
    threadIdRef.current = null;
    lastReportedReadIdRef.current = 0;
    startAttemptRef.current = null;
    failedSendRef.current = null;
    clearBotTyping();
    setSessionToken(null);
    setThread(null);
    setMessages([]);
    setLastSeenSeq(0);
    setPendingBaseline(false);
    setError(null);
    setRefusedFirstMessage(null);
    setPhase('form');
  }, [bizSlug, clearBotTyping]);

  // Resume an existing session on mount (the same chat on every page the visitor
  // browses — the "stays the same across pages" requirement). Skipped when the
  // widget isn't a live chat (email-only / hidden mode).
  useEffect(() => {
    if (!enabled) return;
    const s = loadSession(bizSlug);
    if (s) {
      threadIdRef.current = s.threadId;
      setSessionToken(s.sessionToken);
      if (typeof s.lastSeenSeq === 'number') {
        setLastSeenSeq(s.lastSeenSeq);
      } else {
        // Legacy session: no record of what was read. Treat everything already in
        // the thread as seen (baseline on the first populated poll) rather than
        // alarming the visitor with the whole history as "unread".
        setPendingBaseline(true);
      }
      setPhase('chat');
    }
  }, [bizSlug, enabled]);

  // Poll the thread while we have a session.
  useEffect(() => {
    if (!enabled) return;
    const threadId = threadIdRef.current;
    const token = sessionTokenRef.current;
    if (phase !== 'chat' || threadId == null || !token) return;
    let cancelled = false;
    const doPoll = async () => {
      const res = await pollChatThread({ bizSlug, bizToken, threadId, sessionToken: token, sinceSeq: lastSeqRef.current });
      if (cancelled) return;
      if (!res.success) {
        // A dead stored session 404s every poll — clear it and drop back to the
        // form (see resetToForm). Only on NOT_FOUND: a transient failure must keep
        // the session so polling resumes once the API/network recovers.
        if (res.error.kind === 'NOT_FOUND') resetToForm();
        return;
      }
      setThread(res.data.thread);
      setSupportOnline(res.data.supportOnline);
      if (res.data.messages.length > 0) {
        setMessages((prev) => mergeMessagesById(prev, res.data.messages));
        // A send whose answer was lost may have been stored after all: once the poll brings it,
        // it is no longer a failed message (the composer lets go of its text), and the same text
        // sent again is a new message.
        const failed = failedSendRef.current;
        if (failed && res.data.messages.some((m) => m.clientMsgId === failed.clientMsgId)) {
          failedSendRef.current = null;
          setError(null);
          setRecoveredMessage({ text: failed.text });
        }
      }
    };
    void doPoll();
    // Fast cadence while the typing dots show — the bot's reply lands via this
    // poll, so a tight loop turns "up to 4s of dots after the reply exists" into
    // "at most 2s"; drops back to the normal cadence the moment the dots clear.
    const id = setInterval(
      () => void doPoll(),
      open ? (botTyping ? POLL_BOT_TYPING_MS : POLL_OPEN_MS) : POLL_IDLE_MS,
    );
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [phase, open, botTyping, bizSlug, bizToken, sessionToken, enabled, resetToForm]);

  // Advance the read marker to the latest message when the visitor is reading
  // (panel open) or when a legacy session is establishing its one-time baseline.
  // Only real (positive-id) messages move the marker, so an optimistic echo can't
  // skip past a real message the server hasn't returned yet; the maxSeq > 0 guard
  // also stops an empty thread from consuming the baseline before the history has
  // loaded (opening then closing before the first poll would otherwise strand the
  // marker at 0 and re-count the whole history as unread).
  useEffect(() => {
    const maxSeq = messages.reduce((m, x) => (x.id > 0 && x.threadSeq > m ? x.threadSeq : m), 0);
    if (maxSeq === 0) return;
    if (open || pendingBaseline) {
      setLastSeenSeq((prev) => (maxSeq > prev ? maxSeq : prev));
      if (pendingBaseline) setPendingBaseline(false);

      // Report the read position server-side too (highest REAL message id — an
      // optimistic negative-id placeholder never counts), so the business console
      // can show read receipts on its replies. Fire-and-forget: on failure the
      // high-water mark rolls back so the next poll/open retries the report.
      const threadId = threadIdRef.current;
      const token = sessionTokenRef.current;
      const maxId = messages.reduce((m, x) => (x.id > m ? x.id : m), 0);
      if (threadId != null && token && maxId > lastReportedReadIdRef.current) {
        const prevReported = lastReportedReadIdRef.current;
        lastReportedReadIdRef.current = maxId;
        void markChatRead({ bizSlug, bizToken, threadId, sessionToken: token, lastReadMessageId: maxId }).then(
          (res) => {
            if (!res.success && lastReportedReadIdRef.current === maxId) {
              lastReportedReadIdRef.current = prevReported;
            }
          },
        );
      }
    }
  }, [open, messages, pendingBaseline, bizSlug, bizToken]);

  // Persist the read marker alongside the session so a reload resumes with the
  // correct unread count instead of re-counting the whole history.
  useEffect(() => {
    if (!enabled) return;
    const threadId = threadIdRef.current;
    const token = sessionTokenRef.current;
    if (threadId == null || !token) return;
    saveSession(bizSlug, { sessionToken: token, threadId, lastSeenSeq });
  }, [bizSlug, enabled, lastSeenSeq]);

  const start = useCallback(
    async (form: PreChatForm): Promise<boolean> => {
      setStarting(true);
      setError(null);
      // Reuse the previous attempt's clientMsgId for the SAME text (idempotent
      // re-submit → same thread server-side); a new/edited message gets a fresh one.
      const prior = startAttemptRef.current;
      const clientMsgId =
        prior && prior.text === form.firstMessage ? prior.clientMsgId : crypto.randomUUID();
      startAttemptRef.current = { text: form.firstMessage, clientMsgId };
      // Optimistic echo: a business that lets visitors write straight away shows
      // the conversation pane, so the first message appears at once — with the
      // bot's typing dots under it while the start is in flight. There is no
      // thread yet (thread 0 on the placeholder); the start's answer replaces the
      // whole list. Behind the pre-chat form neither is visible.
      const tempId = optimisticIdRef.current;
      optimisticIdRef.current -= 1;
      setMessages([
        optimisticVisitorMessage({ id: tempId, threadId: 0, threadSeq: 1, bizId: null, clientMsgId, body: form.firstMessage }),
      ]);
      if (aiEnabled) armBotTyping();
      const res = await startChatThread({
        bizSlug,
        bizToken,
        visitorName: form.name?.trim() || undefined,
        visitorEmail: form.email?.trim() || undefined,
        visitorPhoneCountryCode: form.phoneCountryCode?.trim() || undefined,
        visitorPhoneNumber: form.phoneNumber?.trim() || undefined,
        firstMessage: form.firstMessage,
        clientMsgId,
        locale: locale ?? undefined,
        visitorId: getOrCreateVisitorId(),
      });
      setStarting(false);
      if (!res.success) {
        // Nothing was started: the echo goes (the composer gets the text back).
        setMessages([]);
        clearBotTyping();
        if (res.error.code === 'CHAT.CONTACT_DETAILS_REQUIRED') {
          // The business asks for contact details again: the pre-chat form takes over, with the
          // message already in it (no error line: the form says what to do).
          setContactDetailsRequired(true);
          setRefusedFirstMessage(form.firstMessage);
          return false;
        }
        fail(res.error.message);
        return false;
      }
      startAttemptRef.current = null;
      setRefusedFirstMessage(null);
      saveSession(bizSlug, { sessionToken: res.data.sessionToken, threadId: res.data.thread.id, lastSeenSeq: 0 });
      threadIdRef.current = res.data.thread.id;
      lastReportedReadIdRef.current = 0;
      setSessionToken(res.data.sessionToken);
      setThread(res.data.thread);
      setMessages(res.data.messages);
      setSupportOnline(res.data.supportOnline);
      setPhase('chat');
      // The bot's first reply is composed after the response and arrives via the
      // poll — show the dots when one is coming (bot on, thread still bot-driven
      // and not spam-flagged, nothing beyond the visitor's own message returned
      // yet — an idempotent re-submit may return a tail that already carries the
      // reply).
      const newestReal = res.data.messages.reduce<ChatMessage | null>(
        (best, m) => (m.id > 0 && (best === null || m.threadSeq > best.threadSeq) ? m : best),
        null,
      );
      if (
        aiEnabled &&
        isAiAnswerable(res.data.thread.status) &&
        res.data.thread.spamFlaggedAtUtc == null &&
        (newestReal === null || newestReal.senderType === 'public_visitor')
      ) {
        armBotTyping();
      } else {
        clearBotTyping();
      }
      return true;
    },
    [bizSlug, bizToken, locale, aiEnabled, armBotTyping, clearBotTyping, fail],
  );

  const send = useCallback(
    async (text: string): Promise<boolean> => {
      const threadId = threadIdRef.current;
      const token = sessionTokenRef.current;
      if (threadId == null || !token) return false;
      setError(null);
      const failed = failedSendRef.current;
      const clientMsgId = failed && failed.text === text ? failed.clientMsgId : crypto.randomUUID();
      // A new message replaces one that failed: that one was never delivered.
      failedSendRef.current = null;

      // Optimistic echo: show the visitor's own message instantly instead of
      // waiting on the round-trip. The placeholder carries the clientMsgId + a
      // temporary negative id; the real message — arriving via this response OR
      // the next poll — collapses it in mergeMessagesById. The bot's reply is
      // composed AFTER the response and arrives via the poll, under the typing
      // dots (`replies` is only populated by an older in-request-turn API).
      const tempId = optimisticIdRef.current;
      optimisticIdRef.current -= 1;
      setMessages((prev) => {
        const maxSeq = prev.reduce((m, x) => (x.threadSeq > m ? x.threadSeq : m), 0);
        return [
          ...prev,
          optimisticVisitorMessage({
            id: tempId,
            threadId,
            threadSeq: maxSeq + 1,
            bizId: thread?.bizId ?? null,
            clientMsgId,
            body: text,
          }),
        ];
      });

      setSending(true);
      const res = await sendChatMessage({
        bizSlug,
        bizToken,
        threadId,
        sessionToken: token,
        body: text,
        clientMsgId,
        locale: locale ?? undefined,
        visitorId: getOrCreateVisitorId(),
      });
      setSending(false);
      if (res.success) {
        // Merge the visitor echo (and, from an older API, any in-response reply).
        setMessages((prev) => mergeMessagesById(prev, [res.data.message, ...res.data.replies]));
        // The reply is coming via the poll — keep the dots up until it lands
        // (the messages effect clears them; replies from an older API clear them
        // in the same way once merged). `thread` is null on a resumed session
        // before its first poll — arm optimistically then (the reply-clear +
        // the hard expiry keep it honest); a spam-flagged thread gets no dots
        // (the bot is deliberately silent there).
        if (res.data.replies.length === 0 && aiEnabled) {
          const botMayReply =
            thread == null || (isAiAnswerable(thread.status) && thread.spamFlaggedAtUtc == null);
          if (botMayReply) armBotTyping();
        }
        return true;
      }
      if (res.error.kind === 'NOT_FOUND') {
        // The stored session points at a thread that no longer exists (token
        // expired / thread deleted / dev DB swap). Recover to the form instead of
        // stranding the visitor on a dead thread; the optimistic placeholder is
        // discarded with it. In practice the background poll usually heals first.
        resetToForm();
        return false;
      }
      // The send failed — drop the optimistic placeholder so it doesn't look
      // delivered, and surface the error.
      setMessages((prev) => prev.filter((m) => m.clientMsgId !== clientMsgId));
      failedSendRef.current = { text, clientMsgId };
      fail(res.error.message);
      return false;
    },
    [bizSlug, bizToken, locale, thread, resetToForm, aiEnabled, armBotTyping, fail],
  );

  // `startNew` is the user-facing "start a fresh conversation" after a thread
  // closes — the same reset the dead-session auto-heal uses (see resetToForm).
  return {
    phase,
    thread,
    messages,
    supportOnline,
    sending,
    starting,
    botTyping,
    error,
    failureCount,
    contactDetailsRequired,
    refusedFirstMessage,
    recoveredMessage,
    unread,
    closed,
    start,
    send,
    startNew: resetToForm,
  };
}
